import { PersistConfig as ReduxPersistConfig, Persistor, persistReducer, persistStore } from 'redux-persist';
import { createContext } from '@direct-frontend/modules-context';
import type { PersistorOptions, PersistorState, PersistorSubscribeCallback } from 'redux-persist/es/types';
import {
    createEvent,
    deepEqual,
    isObject,
    type JSONCompatible,
    type Replace,
    type Equals,
} from '@direct-frontend/stdlib';

import type { Struct } from 'superstruct';
import { buildCreateSlice, BuildCreateSliceOptions, CreatedSlice, SliceCaseReducers } from '../buildCreateSlice';
import type { AnyAction, CreateSliceOptions, Reducer, Store } from '../rtk-types';
import { createHydrateSliceAction } from '../hydration';
import { MigrationBuilder } from './createPersistMigrate';
import { PERSISTED_FIELDS, type PersistentSliceInternals } from './lib/types';
import { getPersistState } from './lib/getPersistState';

/**
 * Событие, которое позволяет сбросить persistor.
 * Используется для очистки состояния между тестами.
 * @internal
 */
export const resetPersistor = createEvent();

/**
 * Событие, которое позволяет запустить persistor.persist()
 * Используется для повторной гидрации persistent-слайсов после очистки состояния между тестами.
 */
export const persistPersistor = createEvent();

export type ActualPersistor = {
    persist: () => void;
    flush: () => Promise<void>;
    getState: () => PersistorState;
    subscribe: (callback: PersistorSubscribeCallback) => () => void;
};

export const $INTERNALS = Symbol('$INTERNALS');

/**
 * Ре-экспорт методов для создания миграций.
 */
export { createPersistMigrate } from './createPersistMigrate';
export type { PersistentData, InferStateFromMigrations } from './createPersistMigrate';

export type CreatePersistedSlice<
    State extends JSONCompatible<State>,
    PersistentState extends JSONCompatible<PersistentState>,
    GuardedState extends PersistentState,
    CaseReducers extends SliceCaseReducers<
        Replace<JSONCompatible<State>, 'persistentData', JSONCompatible<PersistentState>>
    >,
    Name extends string = string,
> = CreatedSlice<
    Replace<JSONCompatible<State>, 'persistentData', JSONCompatible<PersistentState>>,
    CaseReducers,
    Name
> & {
    [$INTERNALS]: {
        getLoadedPromise: () => Promise<void>;
        persist: PersistConfig<PersistentState, GuardedState>;
        persistor: ActualPersistor;
    };
};

// в типах PersistorOptions нет поля manualPersist, хотя есть обращения к нему
interface FullPersistorOptions extends PersistorOptions {
    manualPersist: boolean;
}

/**
 * Более строго типизированная миграция redux persist
 */
export type PersistMigrate<State, ToState> = (state: State, currentVersion: number) => Promise<ToState | undefined>;

/**
 * Конфигурация persist слоя
 */
export type PersistConfig<
    PersistentState extends JSONCompatible<PersistentState>,
    GuardedState extends PersistentState,
> = Pick<ReduxPersistConfig<PersistentState>, 'storage' | 'stateReconciler'> & {
    migration: MigrationBuilder<PersistentState>;
    validation: Struct<Equals<PersistentState, GuardedState> extends true ? GuardedState : never>;
};

/**
 * Функция для создания слайса с возможностью персиста в модульном сторе.
 *
 * @internal
 */
export type BuiltCreatePersistedSlice = <
    State extends JSONCompatible<State>,
    PersistentState extends JSONCompatible<PersistentState>,
    GuardedState extends PersistentState,
    CaseReducers extends SliceCaseReducers<
        Replace<JSONCompatible<State>, 'persistentData', JSONCompatible<PersistentState>>
    >,
    Name extends string = string,
>(
    options: Omit<
        CreateSliceOptions<
            Replace<JSONCompatible<State>, 'persistentData', JSONCompatible<PersistentState>>,
            CaseReducers,
            Name
        >,
        'extraReducers'
    > & {
        persist: PersistConfig<PersistentState, GuardedState>;
        initialState:
            | (State & { persistentData: NoInfer<PersistentState> })
            | (() => State & { persistentData: NoInfer<PersistentState> });
    } & { selectors?: never }
) => CreatePersistedSlice<State, PersistentState, GuardedState, CaseReducers, Name>;

export type InferPersistentStateFromPersistedSlice<T> =
    T extends CreatePersistedSlice<
        // Не можем использовать any, т.к. он не JSONCompatible
        infer _,
        infer PersistentState,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
        any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
        any
    >
        ? PersistentState
        : never;

export type InferStateFromPersistedSlice<T> =
    T extends CreatePersistedSlice<
        infer State,
        infer PersistentState,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
        any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
        any
    >
        ? Replace<State, 'persistentData', PersistentState>
        : never;

type PersistorContextValue = {
    persistor: Persistor;
    isDiscardingWrites: boolean;
};

type PersistorContext<ModuleName extends string = string> = Record<ModuleName, PersistorContextValue | undefined>;

export const { getContext: getPersistorContext, setContext: setPersistorContext } = createContext<PersistorContext>({
    name: '@direct-frontend/modules-store/persist/persistorContext',
    getInitialValue: () => ({}),
});

/**
 * Создает модульный метод `createPersistedSlice()`.
 *
 * Модульный `createPersistedSlice()`:
 *  1. Создает слайс с опциями createSlice()
 *  2. Добавляет настройку redux-persist
 *  3. Добавляет поле `slice.persistor`, чтобы получить Persistor для отслеживания состояния persist'а
 *
 * @internal
 */
export function buildCreatePersistentSlice<ModuleName extends string>({
    getStore,
    moduleName,
    ...restOptions
}: BuildCreateSliceOptions<ModuleName>): BuiltCreatePersistedSlice {
    return options => {
        const { persist, ...restSliceOptions } = options;

        const { storage, migration, stateReconciler, validation } = persist;

        const version = migration.getVersion();
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const migrate = migration.mergeMigrations(
            validation.is.bind(validation),
            `${moduleName}/${options.name}`
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
        ) as PersistMigrate<any, any>;

        const persistKey = `${moduleName}/${options.name}`;
        const hydrateSliceAction = createHydrateSliceAction(persistKey);

        // Значение, прочитанное из хранилища для стора: его обратная запись пропускается, пока в хранилище
        // этого стора ничего не писали и не удаляли.
        const UNKNOWN_STORAGE_VALUE = Symbol('UNKNOWN_STORAGE_VALUE');
        const readValues = new WeakMap<Store, unknown>();

        const persistStorage: typeof storage = {
            async getItem(key) {
                const store = getStore();
                const value: unknown = await storage.getItem(key);

                if (readValues.get(store) !== UNKNOWN_STORAGE_VALUE) {
                    readValues.set(store, value);
                }

                return value;
            },
            setItem(key, value: unknown) {
                if (getPersistorContext()[moduleName]?.isDiscardingWrites) {
                    return Promise.resolve();
                }

                const store = getStore();

                if (deepEqual(readValues.get(store), value)) {
                    return Promise.resolve();
                }

                // persistoid переиспользует объект stagedState между записями
                const writtenValue = isObject(value) ? { ...value } : value;

                readValues.set(store, UNKNOWN_STORAGE_VALUE);

                return storage.setItem(key, writtenValue);
            },
            removeItem(key) {
                if (getPersistorContext()[moduleName]?.isDiscardingWrites) {
                    return Promise.resolve();
                }

                readValues.set(getStore(), UNKNOWN_STORAGE_VALUE);

                return storage.removeItem(key);
            },
        };

        const wrapSliceReducer = (reducer: Reducer): Reducer => {
            const persistedReducer = persistReducer(
                {
                    version,
                    storage: persistStorage,
                    migrate,
                    stateReconciler,
                    key: persistKey,
                    // Отключаем таймаут, чтобы дожидаться персиста данных. Причина подробно описана в тикете:
                    // https://st.yandex-team.ru/DIRECT-140463#604b62f06035ef521f174153
                    timeout: 0,
                    whitelist: PERSISTED_FIELDS,
                    serialize: false,
                    // Поле не указано в тайпингах redux-persist
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
                    // @ts-expect-error
                    deserialize: false,
                },
                reducer
            );

            /**
             * При гидрации из серверного дампа `_persist` не переносится в стейт:
             * `persistor.persist()` после гидрации регистрирует слайс и поднимает данные из storage.
             */
            return (state: (PersistentSliceInternals & Record<string, unknown>) | undefined, action: AnyAction) => {
                const nextState: PersistentSliceInternals & Record<string, unknown> = persistedReducer(state, action);

                if (hydrateSliceAction.match(action) && !state?._persist && nextState?._persist) {
                    const { _persist, ...hydratedState } = nextState;

                    return hydratedState;
                }

                return nextState;
            };
        };

        const createSlice = buildCreateSlice({ getStore, moduleName, wrapSliceReducer, ...restOptions });
        const slice = createSlice(restSliceOptions);

        const loadedPromises = new WeakMap<Store, Promise<void>>();

        function isLoaded(store: Store) {
            return getPersistState(store.getState()[options.name])?.rehydrated === true;
        }

        /**
         * Промис загрузки слайса: резолвится, когда чтение хранилища завершено —
         * в стейте слайса `_persist.rehydrated: true`.
         */
        function getLoadedPromise() {
            const store = getStore();

            if (isLoaded(store)) {
                return Promise.resolve();
            }

            let loadedPromise = loadedPromises.get(store);

            if (!loadedPromise) {
                loadedPromise = new Promise<void>(resolve => {
                    const unsubscribe = store.subscribe(() => {
                        if (isLoaded(store)) {
                            unsubscribe();
                            loadedPromises.delete(store);
                            resolve();
                        }
                    });
                });
                loadedPromises.set(store, loadedPromise);
            }

            return loadedPromise;
        }

        function getPersistor() {
            const persistorContext = getPersistorContext();
            let result = persistorContext[moduleName]?.persistor;
            if (!result) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                result = persistStore(getStore(), { manualPersist: true } as FullPersistorOptions);
                setPersistorContext({
                    ...persistorContext,
                    [moduleName]: { persistor: result, isDiscardingWrites: false },
                });
            }
            return result;
        }

        let persistor: ActualPersistor = {
            persist() {
                let realPersistor = getPersistor();

                return realPersistor.persist();
            },
            getState() {
                let realPersistor = getPersistor();

                return realPersistor.getState();
            },
            subscribe(callback: PersistorSubscribeCallback) {
                let realPersistor = getPersistor();

                return realPersistor.subscribe(callback);
            },
            async flush() {
                let realPersistor = getPersistor();

                return realPersistor.flush();
            },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error - hide from public api, only for tests
            destroy() {
                const realPersistor = getPersistor();

                let persistorContext = getPersistorContext();
                setPersistorContext({
                    ...persistorContext,
                    [moduleName]: { persistor: realPersistor, isDiscardingWrites: true },
                });
                try {
                    realPersistor.flush();
                } finally {
                    persistorContext = getPersistorContext();
                    setPersistorContext({
                        ...persistorContext,
                        [moduleName]: { persistor: realPersistor, isDiscardingWrites: false },
                    });
                }

                // Ставим на паузу, чтобы отложенные регидрации не выполнялись
                realPersistor.pause();

                persistorContext = getPersistorContext();
                setPersistorContext({ ...persistorContext, [moduleName]: undefined });
            },
        };

        if (process.env.BUILD_TARGET === 'client') {
            persistor.persist();
        }

        resetPersistor.subscribe(() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error - hide from public api, only for tests
            persistor.destroy();
        });

        persistPersistor.subscribe(() => {
            persistor.persist();
        });

        return {
            ...slice,
            [$INTERNALS]: {
                persist,
                persistor,
                getLoadedPromise,
            },
        };
    };
}
